import React, { useEffect, useState, useMemo } from 'react';
import {
  Activity,
  ArrowLeft,
  Calculator,
  Check,
  ClipboardList,
  FileText,
  HeartPulse,
  Mail,
  Phone,
  Save,
  Search,
  Star,
  Trash2,
  TrendingDown,
  UserPlus,
  Users,
  Utensils,
  X,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import MealPlanBuilder from './MealPlanBuilder';

const ACTIVITY_FACTORS = [
  { id: 'sedentary', label: 'Sedentary (desk job, little exercise) · ×1.2', mult: 1.2 },
  { id: 'lightly_active', label: 'Lightly Active (1–3 workouts/week) · ×1.375', mult: 1.375 },
  { id: 'moderately_active', label: 'Moderately Active (3–5 workouts/week) · ×1.55', mult: 1.55 },
  { id: 'very_active', label: 'Very Active (6–7 workouts/week) · ×1.725', mult: 1.725 },
  { id: 'athlete', label: 'Endurance / Athlete (2x daily training) · ×1.9', mult: 1.9 },
];

const calcAge = (birthDateStr) => {
  if (!birthDateStr) return 32;
  const d = new Date(birthDateStr);
  if (Number.isNaN(d.getTime())) return 32;
  const diff = Date.now() - d.getTime();
  return Math.max(1, Math.floor(diff / (365.25 * 24 * 3600 * 1000)));
};

// Simple responsive SVG trend chart for Weight & Body Fat %
const ProgressTrendChart = ({ measurements }) => {
  if (!measurements || measurements.length < 2) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center text-xs text-gray-400">
        Log at least 2 measurements on different dates to visualize weight and body-fat trend curves.
      </div>
    );
  }

  const pts = measurements.filter((m) => m.weight_kg);
  if (pts.length < 2) return null;

  const weights = pts.map((p) => Number(p.weight_kg));
  const minW = Math.min(...weights) - 1;
  const maxW = Math.max(...weights) + 1;
  const width = 560;
  const height = 160;
  const padX = 40;
  const padY = 24;

  const coords = pts.map((p, idx) => {
    const x = padX + (idx / Math.max(1, pts.length - 1)) * (width - padX * 2);
    const y =
      height -
      padY -
      ((Number(p.weight_kg) - minW) / Math.max(0.5, maxW - minW)) * (height - padY * 2);
    return { x, y, label: p.measured_at?.slice(5) || '', val: p.weight_kg, bf: p.body_fat_pct };
  });

  const polyline = coords.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase text-gray-500 flex items-center gap-1.5">
          <TrendingDown className="w-4 h-4 text-emerald-600" />
          Weight Progression (kg)
        </span>
        <span className="text-xs font-bold text-emerald-700">
          {(weights[weights.length - 1] - weights[0]).toFixed(1)} kg net change
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40 overflow-visible">
        <polyline
          fill="none"
          stroke="#10b981"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={polyline}
        />
        {coords.map((c, idx) => (
          <g key={idx}>
            <circle cx={c.x} cy={c.y} r="5" fill="#059669" stroke="#fff" strokeWidth="2" />
            <text
              x={c.x}
              y={c.y - 10}
              textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill="#0f172a"
            >
              {c.val}kg
            </text>
            <text x={c.x} y={height - 4} textAnchor="middle" fontSize="10" fill="#64748b">
              {c.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const PatientsManager = ({ initialSelectedPatient = null, onClearInitialPatient }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(initialSelectedPatient);
  const [subTab, setSubTab] = useState('anamnesis'); // 'anamnesis' | 'notes' | 'measurements' | 'calculator' | 'mealplans'
  const [feedback, setFeedback] = useState('');

  // New patient modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    birth_date: '1992-06-15',
    sex: 'female',
    occupation: '',
    preferred_language: 'es',
    tagsText: 'Weight Management, Mediterranean',
    primary_goal: '',
  });

  // Patient sub-records
  const [notes, setNotes] = useState([]);
  const [measurements, setMeasurements] = useState([]);

  // New SOAP note state
  const [noteForm, setNoteForm] = useState({
    session_date: new Date().toISOString().slice(0, 10),
    session_type: 'Follow-up',
    adherence_score: 5,
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    private_notes: '',
  });

  // New Measurement state
  const [measForm, setMeasForm] = useState({
    measured_at: new Date().toISOString().slice(0, 10),
    weight_kg: '',
    height_cm: '165',
    body_fat_pct: '',
    muscle_mass_kg: '',
    waist_cm: '',
    hip_cm: '',
    arm_cm: '',
    thigh_cm: '',
    glucose_mg_dl: '',
    hba1c_pct: '',
    cholesterol_total: '',
    vitamin_d_ng_ml: '',
    ferritin_ng_ml: '',
    tsh_miu_l: '',
    notes: '',
  });

  // TDEE Calculator state
  const [calcState, setCalcState] = useState({
    formula: 'mifflin',
    weight_kg: 66,
    height_cm: 165,
    age: 32,
    sex: 'female',
    activity: 'moderately_active',
    kcalAdjustment: -300,
    proteinPct: 25,
    carbsPct: 45,
    fatPct: 30,
    fiber_g: 32,
    water_liters: 2.2,
  });

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const loadPatients = async () => {
    setLoading(true);
    try {
      const list = await adminApi.getPatients(search, statusFilter);
      setPatients(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  useEffect(() => {
    if (initialSelectedPatient) {
      setSelectedPatient(initialSelectedPatient);
      if (onClearInitialPatient) onClearInitialPatient();
    }
  }, [initialSelectedPatient, onClearInitialPatient]);

  // Load notes & measurements whenever a patient is opened
  useEffect(() => {
    if (!selectedPatient) return;
    Promise.all([
      adminApi.getPatientNotes(selectedPatient.id),
      adminApi.getPatientMeasurements(selectedPatient.id),
    ]).then(([nList, mList]) => {
      setNotes(nList);
      setMeasurements(mList);
      const latestM = mList[mList.length - 1];
      setCalcState((prev) => ({
        ...prev,
        weight_kg: latestM?.weight_kg || 66,
        height_cm: latestM?.height_cm || 165,
        age: calcAge(selectedPatient.birth_date),
        sex: selectedPatient.sex || 'female',
        activity: selectedPatient.clinical_profile?.activity_level || 'moderately_active',
      }));
    });
  }, [selectedPatient]);

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    const tags = newPatientForm.tagsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const created = await adminApi.createPatient({
      full_name: newPatientForm.full_name.trim(),
      email: newPatientForm.email.trim() || null,
      phone: newPatientForm.phone.trim() || null,
      birth_date: newPatientForm.birth_date || null,
      sex: newPatientForm.sex,
      occupation: newPatientForm.occupation.trim() || null,
      preferred_language: newPatientForm.preferred_language,
      status: 'active',
      tags,
      clinical_profile: {
        primary_goal: newPatientForm.primary_goal.trim(),
        activity_level: 'moderately_active',
      },
      target_kcal: 1800,
      target_macros: {
        protein_g: 115,
        carbs_g: 175,
        fat_g: 65,
        fiber_g: 30,
        water_liters: 2.2,
      },
    });
    setPatients((prev) => [created, ...prev]);
    setShowNewModal(false);
    setSelectedPatient(created);
    setSubTab('anamnesis');
    flash(`Created patient file for ${created.full_name}`);
  };

  const handleSavePatientProfile = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;
    const saved = await adminApi.updatePatient(selectedPatient.id, {
      full_name: selectedPatient.full_name,
      email: selectedPatient.email,
      phone: selectedPatient.phone,
      birth_date: selectedPatient.birth_date,
      sex: selectedPatient.sex,
      occupation: selectedPatient.occupation,
      preferred_language: selectedPatient.preferred_language,
      status: selectedPatient.status,
      tags: selectedPatient.tags,
      clinical_profile: selectedPatient.clinical_profile,
      target_kcal: selectedPatient.target_kcal,
      target_macros: selectedPatient.target_macros,
    });
    setSelectedPatient(saved);
    setPatients((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
    flash('Patient clinical profile saved!');
  };

  const handleDeletePatient = async (patient) => {
    if (!window.confirm(`Delete patient file for "${patient.full_name}"?`)) return;
    await adminApi.deletePatient(patient.id);
    setPatients((prev) => prev.filter((p) => p.id !== patient.id));
    if (selectedPatient?.id === patient.id) setSelectedPatient(null);
    flash('Patient file deleted.');
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;
    const created = await adminApi.createPatientNote(selectedPatient.id, noteForm);
    setNotes((prev) => [created, ...prev]);
    setNoteForm({
      session_date: new Date().toISOString().slice(0, 10),
      session_type: 'Follow-up',
      adherence_score: 5,
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      private_notes: '',
    });
    flash('SOAP consultation note logged!');
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedPatient) return;
    await adminApi.deletePatientNote(selectedPatient.id, noteId);
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    flash('Consultation note removed.');
  };

  const handleAddMeasurement = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;
    const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const lab_markers = {};
    [
      'glucose_mg_dl',
      'hba1c_pct',
      'cholesterol_total',
      'vitamin_d_ng_ml',
      'ferritin_ng_ml',
      'tsh_miu_l',
    ].forEach((k) => {
      if (measForm[k] !== '') lab_markers[k] = Number(measForm[k]);
    });

    const created = await adminApi.createPatientMeasurement(selectedPatient.id, {
      measured_at: measForm.measured_at,
      weight_kg: numOrNull(measForm.weight_kg),
      height_cm: numOrNull(measForm.height_cm),
      body_fat_pct: numOrNull(measForm.body_fat_pct),
      muscle_mass_kg: numOrNull(measForm.muscle_mass_kg),
      waist_cm: numOrNull(measForm.waist_cm),
      hip_cm: numOrNull(measForm.hip_cm),
      arm_cm: numOrNull(measForm.arm_cm),
      thigh_cm: numOrNull(measForm.thigh_cm),
      lab_markers,
      notes: measForm.notes.trim() || null,
    });
    setMeasurements((prev) =>
      [...prev, created].sort((a, b) => (a.measured_at || '').localeCompare(b.measured_at || ''))
    );
    flash('Anthropometric & lab measurement saved!');
  };

  const handleDeleteMeasurement = async (measId) => {
    if (!selectedPatient) return;
    await adminApi.deletePatientMeasurement(selectedPatient.id, measId);
    setMeasurements((prev) => prev.filter((m) => m.id !== measId));
    flash('Measurement deleted.');
  };

  // ── BMR / TDEE Calculation ──
  const tdeeResult = useMemo(() => {
    const w = Number(calcState.weight_kg) || 65;
    const h = Number(calcState.height_cm) || 165;
    const a = Number(calcState.age) || 32;
    const isMale = calcState.sex === 'male';

    let bmr;
    if (calcState.formula === 'harris') {
      bmr = isMale
        ? 88.362 + 13.397 * w + 4.799 * h - 5.677 * a
        : 447.593 + 9.247 * w + 3.098 * h - 4.33 * a;
    } else {
      // Mifflin-St Jeor (Gold standard)
      bmr = 10 * w + 6.25 * h - 5 * a + (isMale ? 5 : -161);
    }

    const actObj =
      ACTIVITY_FACTORS.find((f) => f.id === calcState.activity) || ACTIVITY_FACTORS[2];
    const tdee = Math.round(bmr * actObj.mult);
    const targetKcal = Math.max(1100, tdee + Number(calcState.kcalAdjustment || 0));

    const protein_g = Math.round((targetKcal * (Number(calcState.proteinPct) / 100)) / 4);
    const carbs_g = Math.round((targetKcal * (Number(calcState.carbsPct) / 100)) / 4);
    const fat_g = Math.round((targetKcal * (Number(calcState.fatPct) / 100)) / 9);

    return {
      bmr: Math.round(bmr),
      tdee,
      targetKcal,
      protein_g,
      carbs_g,
      fat_g,
      proteinPerKg: (protein_g / Math.max(1, w)).toFixed(1),
    };
  }, [calcState]);

  const applyCalculatorToPatient = async () => {
    if (!selectedPatient) return;
    const updatedMacros = {
      protein_g: tdeeResult.protein_g,
      carbs_g: tdeeResult.carbs_g,
      fat_g: tdeeResult.fat_g,
      fiber_g: Number(calcState.fiber_g) || 30,
      water_liters: Number(calcState.water_liters) || 2.2,
    };
    const saved = await adminApi.updatePatient(selectedPatient.id, {
      target_kcal: tdeeResult.targetKcal,
      target_macros: updatedMacros,
    });
    setSelectedPatient(saved);
    setPatients((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
    flash(`Saved ${tdeeResult.targetKcal} kcal & macro targets to ${saved.full_name}'s file!`);
  };

  // ── VIEW 1: Patient 360° Workspace ──────────────────────────────────────────
  if (selectedPatient) {
    const cp = selectedPatient.clinical_profile || {};
    const updateCp = (field, val) => {
      setSelectedPatient((prev) => ({
        ...prev,
        clinical_profile: { ...(prev.clinical_profile || {}), [field]: val },
      }));
    };
    const latestMeas = measurements[measurements.length - 1];

    return (
      <div className="space-y-6">
        {feedback && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <Check className="w-4 h-4" />
            {feedback}
          </div>
        )}

        {/* Patient 360 Header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setSelectedPatient(null)}
                className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                title="Back to Patients Roster"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-black text-lg flex items-center justify-center shadow-sm">
                {(selectedPatient.full_name || 'P').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-extrabold text-gray-900">
                    {selectedPatient.full_name}
                  </h2>
                  <span className="text-xs font-bold uppercase px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    {selectedPatient.status || 'active'}
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-primary uppercase">
                    {(selectedPatient.preferred_language || 'es').toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                  {selectedPatient.email && (
                    <span className="inline-flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> {selectedPatient.email}
                    </span>
                  )}
                  {selectedPatient.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" /> {selectedPatient.phone}
                    </span>
                  )}
                  <span>· Age: {calcAge(selectedPatient.birth_date)} yrs</span>
                  {selectedPatient.occupation && <span>· {selectedPatient.occupation}</span>}
                </div>
              </div>
            </div>

            {/* Quick KPI Pills */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="bg-gray-50 border border-gray-200/80 rounded-xl px-3.5 py-2 text-center">
                <div className="text-[11px] font-bold uppercase text-gray-400">Latest Weight</div>
                <div className="text-sm font-extrabold text-gray-900">
                  {latestMeas?.weight_kg ? `${latestMeas.weight_kg} kg` : '—'}
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200/80 rounded-xl px-3.5 py-2 text-center">
                <div className="text-[11px] font-bold uppercase text-gray-400">Body Fat</div>
                <div className="text-sm font-extrabold text-gray-900">
                  {latestMeas?.body_fat_pct ? `${latestMeas.body_fat_pct}%` : '—'}
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-2 text-center">
                <div className="text-[11px] font-bold uppercase text-emerald-700">Daily Target</div>
                <div className="text-sm font-extrabold text-emerald-900">
                  {selectedPatient.target_kcal || 1800} kcal
                </div>
              </div>
            </div>
          </div>

          {/* Workspace Sub-Navigation */}
          <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-100">
            {[
              { id: 'anamnesis', label: '1. Clinical Anamnesis', icon: ClipboardList },
              { id: 'notes', label: `2. Consultations & SOAP (${notes.length})`, icon: FileText },
              {
                id: 'measurements',
                label: `3. Anthropometrics & Labs (${measurements.length})`,
                icon: Activity,
              },
              { id: 'calculator', label: '4. Energy & Macro Calculator', icon: Calculator },
              { id: 'mealplans', label: '5. Meal Plan & PDF Export', icon: Utensils },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setSubTab(id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  subTab === id
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* SUB-TAB 1: Clinical & Dietary Anamnesis */}
        {subTab === 'anamnesis' && (
          <form
            onSubmit={handleSavePatientProfile}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Patient Anamnesis, Medical History & Dietary Habits
                </h3>
                <p className="text-xs text-gray-500">
                  Comprehensive clinical record following professional nutrition practice standards.
                </p>
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm"
              >
                <Save className="w-4 h-4" />
                Save Clinical File
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={selectedPatient.full_name || ''}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, full_name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-semibold"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={selectedPatient.email || ''}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, email: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Phone / WhatsApp
                </label>
                <input
                  type="text"
                  value={selectedPatient.phone || ''}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, phone: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Birth Date
                </label>
                <input
                  type="date"
                  value={selectedPatient.birth_date || ''}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, birth_date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Biological Sex
                </label>
                <select
                  value={selectedPatient.sex || 'female'}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, sex: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Preferred Language
                </label>
                <select
                  value={selectedPatient.preferred_language || 'es'}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, preferred_language: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="es">🇪🇸 Español</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="it">🇮🇹 Italiano</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Occupation / Schedule
                </label>
                <input
                  type="text"
                  value={selectedPatient.occupation || ''}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, occupation: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Patient Status
                </label>
                <select
                  value={selectedPatient.status || 'active'}
                  onChange={(e) =>
                    setSelectedPatient({ ...selectedPatient, status: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Primary Nutritional Goal & Motivation
                </label>
                <textarea
                  rows={2}
                  value={cp.primary_goal || ''}
                  onChange={(e) => updateCp('primary_goal', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Weight management, body recomposition, glycemic control, sports performance…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Pathologies, Diagnoses & Medical History
                </label>
                <textarea
                  rows={2}
                  value={cp.medical_conditions || ''}
                  onChange={(e) => updateCp('medical_conditions', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Insulin resistance, PCOS, hypothyroidism, hypertension, dyslipidemia…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Food Allergies & Intolerances
                </label>
                <textarea
                  rows={2}
                  value={cp.allergies_intolerances || ''}
                  onChange={(e) => updateCp('allergies_intolerances', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Celiac/gluten, lactose, nuts, shellfish, FODMAP triggers…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Current Medications & Supplements
                </label>
                <textarea
                  rows={2}
                  value={cp.medications_supplements || ''}
                  onChange={(e) => updateCp('medications_supplements', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Vitamin D3, Omega-3, Magnesium, Metformin, Levothyroxine…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Digestive Health, Sleep & Lifestyle
                </label>
                <textarea
                  rows={2}
                  value={cp.digestive_health || ''}
                  onChange={(e) => updateCp('digestive_health', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Bowel regularity, bloating, reflux, sleep hours/quality, stress level…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  24-Hour Dietary Recall & Food Preferences
                </label>
                <textarea
                  rows={2}
                  value={cp.dietary_recall_24h || ''}
                  onChange={(e) => updateCp('dietary_recall_24h', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm"
                  placeholder="Typical breakfast, lunch, snacks, dinner, favorite foods & disliked foods…"
                />
              </div>
            </div>
          </form>
        )}

        {/* SUB-TAB 2: Consultations & SOAP Notes */}
        {subTab === 'notes' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <form
              onSubmit={handleAddNote}
              className="lg:col-span-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4 self-start"
            >
              <h3 className="text-base font-bold text-gray-900">New Consultation SOAP Note</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={noteForm.session_date}
                    onChange={(e) => setNoteForm({ ...noteForm, session_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Type
                  </label>
                  <select
                    value={noteForm.session_type}
                    onChange={(e) => setNoteForm({ ...noteForm, session_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white"
                  >
                    <option value="Initial">Initial Assessment</option>
                    <option value="Follow-up">Follow-up</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Adherence (1–5)
                  </label>
                  <select
                    value={noteForm.adherence_score}
                    onChange={(e) =>
                      setNoteForm({ ...noteForm, adherence_score: Number(e.target.value) })
                    }
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white"
                  >
                    {[5, 4, 3, 2, 1].map((s) => (
                      <option key={s} value={s}>
                        {'★'.repeat(s)} ({s}/5)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-700 uppercase mb-1">
                  S — Subjective (Patient Feedback, Hunger, Energy, Cravings)
                </label>
                <textarea
                  rows={2}
                  required
                  value={noteForm.subjective}
                  onChange={(e) => setNoteForm({ ...noteForm, subjective: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-blue-700 uppercase mb-1">
                  O — Objective (Weight Delta, Anthropometrics, Labs)
                </label>
                <textarea
                  rows={2}
                  value={noteForm.objective}
                  onChange={(e) => setNoteForm({ ...noteForm, objective: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-amber-700 uppercase mb-1">
                  A — Assessment (Nutritional Diagnosis & Progress Evaluation)
                </label>
                <textarea
                  rows={2}
                  value={noteForm.assessment}
                  onChange={(e) => setNoteForm({ ...noteForm, assessment: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-purple-700 uppercase mb-1">
                  P — Plan (Next Steps, Dietary Adjustments & Behavioral Goals)
                </label>
                <textarea
                  rows={3}
                  value={noteForm.plan}
                  onChange={(e) => setNoteForm({ ...noteForm, plan: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gray-900 hover:bg-primary text-white text-xs font-bold py-2.5 rounded-xl shadow-sm transition-colors"
              >
                + Save SOAP Consultation Note
              </button>
            </form>

            <div className="lg:col-span-7 space-y-4">
              {notes.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-sm text-gray-400">
                  No consultation notes recorded yet for {selectedPatient.full_name}.
                </div>
              ) : (
                notes.map((n) => (
                  <div
                    key={n.id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3"
                  >
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800">
                          {n.session_type}
                        </span>
                        <span className="font-bold text-sm text-gray-900">{n.session_date}</span>
                        {n.adherence_score && (
                          <span className="inline-flex items-center text-amber-500 text-xs font-bold">
                            <Star className="w-3.5 h-3.5 fill-amber-400 mr-0.5" />
                            {n.adherence_score}/5
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(n.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      {n.subjective && (
                        <div className="bg-emerald-50/50 rounded-xl p-3">
                          <strong className="text-emerald-800 block mb-0.5">Subjective:</strong>
                          <p className="text-gray-700 whitespace-pre-line">{n.subjective}</p>
                        </div>
                      )}
                      {n.objective && (
                        <div className="bg-blue-50/50 rounded-xl p-3">
                          <strong className="text-blue-800 block mb-0.5">Objective:</strong>
                          <p className="text-gray-700 whitespace-pre-line">{n.objective}</p>
                        </div>
                      )}
                      {n.assessment && (
                        <div className="bg-amber-50/50 rounded-xl p-3">
                          <strong className="text-amber-800 block mb-0.5">Assessment:</strong>
                          <p className="text-gray-700 whitespace-pre-line">{n.assessment}</p>
                        </div>
                      )}
                      {n.plan && (
                        <div className="bg-purple-50/50 rounded-xl p-3">
                          <strong className="text-purple-800 block mb-0.5">Action Plan:</strong>
                          <p className="text-gray-700 whitespace-pre-line">{n.plan}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* SUB-TAB 3: Anthropometrics, Body Composition & Lab Biomarkers */}
        {subTab === 'measurements' && (
          <div className="space-y-6">
            <ProgressTrendChart measurements={measurements} />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <form
                onSubmit={handleAddMeasurement}
                className="lg:col-span-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4 self-start"
              >
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <HeartPulse className="w-4 h-4 text-emerald-600" />
                  Log Anthropometrics & Biomarkers
                </h3>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={measForm.measured_at}
                      onChange={(e) => setMeasForm({ ...measForm, measured_at: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Weight (kg) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={measForm.weight_kg}
                      onChange={(e) => setMeasForm({ ...measForm, weight_kg: e.target.value })}
                      placeholder="65.4"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={measForm.height_cm}
                      onChange={(e) => setMeasForm({ ...measForm, height_cm: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Body Fat (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={measForm.body_fat_pct}
                      onChange={(e) => setMeasForm({ ...measForm, body_fat_pct: e.target.value })}
                      placeholder="27.5"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Muscle (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={measForm.muscle_mass_kg}
                      onChange={(e) =>
                        setMeasForm({ ...measForm, muscle_mass_kg: e.target.value })
                      }
                      placeholder="45.0"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Waist (cm)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={measForm.waist_cm}
                      onChange={(e) => setMeasForm({ ...measForm, waist_cm: e.target.value })}
                      placeholder="73.5"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Hip (cm)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={measForm.hip_cm}
                      onChange={(e) => setMeasForm({ ...measForm, hip_cm: e.target.value })}
                      placeholder="97.0"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Glucose (mg/dL)
                    </label>
                    <input
                      type="number"
                      step="1"
                      value={measForm.glucose_mg_dl}
                      onChange={(e) =>
                        setMeasForm({ ...measForm, glucose_mg_dl: e.target.value })
                      }
                      placeholder="89"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      HbA1c (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={measForm.hba1c_pct}
                      onChange={(e) => setMeasForm({ ...measForm, hba1c_pct: e.target.value })}
                      placeholder="5.2"
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-2.5 rounded-xl shadow-sm"
                >
                  + Record Measurement
                </button>
              </form>

              <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 overflow-x-auto">
                <h3 className="text-base font-bold text-gray-900 mb-4">
                  Historical Anthropometrics & Lab Panel
                </h3>
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-400 uppercase">
                      <th className="py-2.5 px-2">Date</th>
                      <th className="py-2.5 px-2">Weight</th>
                      <th className="py-2.5 px-2">BMI</th>
                      <th className="py-2.5 px-2">Body Fat</th>
                      <th className="py-2.5 px-2">Muscle</th>
                      <th className="py-2.5 px-2">Waist / Hip</th>
                      <th className="py-2.5 px-2">Labs</th>
                      <th className="py-2.5 px-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {measurements.map((m) => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="py-2.5 px-2 font-bold text-gray-900">{m.measured_at}</td>
                        <td className="py-2.5 px-2 font-bold text-emerald-700">
                          {m.weight_kg ? `${m.weight_kg} kg` : '—'}
                        </td>
                        <td className="py-2.5 px-2">{m.bmi || '—'}</td>
                        <td className="py-2.5 px-2">
                          {m.body_fat_pct ? `${m.body_fat_pct}%` : '—'}
                        </td>
                        <td className="py-2.5 px-2">
                          {m.muscle_mass_kg ? `${m.muscle_mass_kg} kg` : '—'}
                        </td>
                        <td className="py-2.5 px-2">
                          {m.waist_cm || '—'} / {m.hip_cm || '—'} cm
                        </td>
                        <td className="py-2.5 px-2 text-gray-500">
                          {m.lab_markers?.glucose_mg_dl && `Glu:${m.lab_markers.glucose_mg_dl} `}
                          {m.lab_markers?.hba1c_pct && `A1c:${m.lab_markers.hba1c_pct}% `}
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteMeasurement(m.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* SUB-TAB 4: Energy (BMR / TDEE) & Macronutrient Calculator */}
        {subTab === 'calculator' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Clinical Energy Expenditure (BMR / TDEE) & Macro Target Calculator
                </h3>
                <p className="text-xs text-gray-500">
                  Calculate resting and total daily energy expenditure and save personalized macro targets to{' '}
                  {selectedPatient.full_name}'s file.
                </p>
              </div>
              <button
                type="button"
                onClick={applyCalculatorToPatient}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm"
              >
                <Save className="w-4 h-4" />
                Save as Patient's Active Targets
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Predictive Equation
                  </label>
                  <select
                    value={calcState.formula}
                    onChange={(e) => setCalcState({ ...calcState, formula: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white font-semibold"
                  >
                    <option value="mifflin">Mifflin-St Jeor (Clinical Standard)</option>
                    <option value="harris">Harris-Benedict (Revised)</option>
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={calcState.weight_kg}
                      onChange={(e) => setCalcState({ ...calcState, weight_kg: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      value={calcState.height_cm}
                      onChange={(e) => setCalcState({ ...calcState, height_cm: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                      Age
                    </label>
                    <input
                      type="number"
                      value={calcState.age}
                      onChange={(e) => setCalcState({ ...calcState, age: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Physical Activity Multiplier (PAL)
                  </label>
                  <select
                    value={calcState.activity}
                    onChange={(e) => setCalcState({ ...calcState, activity: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs bg-white font-semibold"
                  >
                    {ACTIVITY_FACTORS.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Caloric Deficit / Surplus Adjustment ({calcState.kcalAdjustment} kcal)
                  </label>
                  <input
                    type="range"
                    min="-700"
                    max="600"
                    step="50"
                    value={calcState.kcalAdjustment}
                    onChange={(e) =>
                      setCalcState({ ...calcState, kcalAdjustment: Number(e.target.value) })
                    }
                    className="w-full accent-emerald-600"
                  />
                  <div className="flex justify-between text-[11px] text-gray-400">
                    <span>-700 kcal (Fat Loss)</span>
                    <span>0 (Maintenance)</span>
                    <span>+600 kcal (Hypertrophy)</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-bold text-blue-600 mb-1">
                      Protein %
                    </label>
                    <input
                      type="number"
                      value={calcState.proteinPct}
                      onChange={(e) =>
                        setCalcState({ ...calcState, proteinPct: Number(e.target.value) })
                      }
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-amber-600 mb-1">
                      Carbs %
                    </label>
                    <input
                      type="number"
                      value={calcState.carbsPct}
                      onChange={(e) =>
                        setCalcState({ ...calcState, carbsPct: Number(e.target.value) })
                      }
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-purple-600 mb-1">
                      Fats %
                    </label>
                    <input
                      type="number"
                      value={calcState.fatPct}
                      onChange={(e) =>
                        setCalcState({ ...calcState, fatPct: Number(e.target.value) })
                      }
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-5 flex flex-col justify-between">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Resting Metabolic Rate (BMR):</span>
                    <strong className="text-gray-900">{tdeeResult.bmr} kcal/day</strong>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Maintenance Expenditure (TDEE):</span>
                    <strong className="text-gray-900">{tdeeResult.tdee} kcal/day</strong>
                  </div>
                  <div className="pt-2 border-t border-emerald-200 flex justify-between items-baseline">
                    <span className="text-xs font-bold uppercase text-emerald-800">
                      Prescribed Target:
                    </span>
                    <span className="text-2xl font-black text-emerald-900">
                      {tdeeResult.targetKcal} kcal
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-emerald-200 text-center">
                  <div className="bg-white rounded-xl p-2">
                    <div className="text-xs font-bold text-blue-600">Protein</div>
                    <div className="text-sm font-black text-gray-900">{tdeeResult.protein_g}g</div>
                    <div className="text-[10px] text-gray-400">{tdeeResult.proteinPerKg} g/kg</div>
                  </div>
                  <div className="bg-white rounded-xl p-2">
                    <div className="text-xs font-bold text-amber-600">Carbs</div>
                    <div className="text-sm font-black text-gray-900">{tdeeResult.carbs_g}g</div>
                    <div className="text-[10px] text-gray-400">{calcState.carbsPct}%</div>
                  </div>
                  <div className="bg-white rounded-xl p-2">
                    <div className="text-xs font-bold text-purple-600">Fats</div>
                    <div className="text-sm font-black text-gray-900">{tdeeResult.fat_g}g</div>
                    <div className="text-[10px] text-gray-400">{calcState.fatPct}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUB-TAB 5: Interactive Meal Plan Builder & PDF Export for this Patient */}
        {subTab === 'mealplans' && <MealPlanBuilder patient={selectedPatient} />}
      </div>
    );
  }

  // ── VIEW 2: Patients Roster & Directory ─────────────────────────────────────
  return (
    <div className="space-y-6">
      {feedback && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {feedback}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Patients & Clinical CRM ({patients.length})
              </h2>
              <p className="text-xs text-gray-500">
                Manage patient files, clinical anamnesis, SOAP consultation notes, anthropometrics, and personalized meal plans.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            New Patient File
          </button>
        </div>

        {/* Search & Status Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patients by name, email, or phone…"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm"
            />
          </div>

          <div className="flex gap-1.5">
            {['all', 'active', 'paused', 'archived'].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold capitalize transition-colors ${
                  statusFilter === st
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400 py-10 text-center">Loading patients…</p>
        ) : patients.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <p className="text-sm">No patients match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patients.map((p) => (
              <div
                key={p.id}
                onClick={() => {
                  setSelectedPatient(p);
                  setSubTab('anamnesis');
                }}
                className="cursor-pointer group border border-gray-200 hover:border-emerald-400 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col justify-between gap-4 bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 font-black text-base flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      {(p.full_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                        {p.full_name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {p.email || 'No email'} {p.phone ? `· ${p.phone}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {p.target_kcal || 1800} kcal
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePatient(p);
                      }}
                      className="p-1.5 text-gray-300 hover:text-red-600 rounded-lg"
                      title="Delete patient"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {p.clinical_profile?.primary_goal && (
                  <p className="text-xs text-gray-600 line-clamp-2 bg-gray-50 rounded-xl p-2.5">
                    🎯 {p.clinical_profile.primary_goal}
                  </p>
                )}

                <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                  <div className="flex flex-wrap gap-1.5">
                    {(p.tags || []).map((t) => (
                      <span
                        key={t}
                        className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-primary"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <span className="text-xs font-bold text-emerald-600 group-hover:translate-x-0.5 transition-transform">
                    Open 360° File →
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create New Patient Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={handleCreatePatient}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-lg w-full p-6 space-y-4"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-gray-900">Create New Patient File</h3>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                className="p-1 text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={newPatientForm.full_name}
                  onChange={(e) =>
                    setNewPatientForm({ ...newPatientForm, full_name: e.target.value })
                  }
                  placeholder="Lucía Fernández"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newPatientForm.email}
                  onChange={(e) => setNewPatientForm({ ...newPatientForm, email: e.target.value })}
                  placeholder="lucia@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Phone / WhatsApp
                </label>
                <input
                  type="text"
                  value={newPatientForm.phone}
                  onChange={(e) => setNewPatientForm({ ...newPatientForm, phone: e.target.value })}
                  placeholder="+34 600 000 000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Birth Date
                </label>
                <input
                  type="date"
                  value={newPatientForm.birth_date}
                  onChange={(e) =>
                    setNewPatientForm({ ...newPatientForm, birth_date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Preferred Language
                </label>
                <select
                  value={newPatientForm.preferred_language}
                  onChange={(e) =>
                    setNewPatientForm({ ...newPatientForm, preferred_language: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="es">🇪🇸 Español</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="it">🇮🇹 Italiano</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Clinical Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={newPatientForm.tagsText}
                  onChange={(e) =>
                    setNewPatientForm({ ...newPatientForm, tagsText: e.target.value })
                  }
                  placeholder="Weight Loss, Mediterranean, Gluten-Free"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Primary Goal / Reason for Consultation
                </label>
                <textarea
                  rows={2}
                  value={newPatientForm.primary_goal}
                  onChange={(e) =>
                    setNewPatientForm({ ...newPatientForm, primary_goal: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2 rounded-lg"
              >
                Create Patient File
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PatientsManager;
